import { useEffect, useState } from 'react';

import { cn } from '~/styling';

const LOADING_MESSAGES = [
  'Locating coordinates...',
  'Fetching street data...',
  'Mapping roads...',
  'Adding water features...',
  'Rendering parks...',
  'Applying theme colors...',
  'Drawing the map...',
  'Almost there...',
];

// Listed cities render in a few seconds; past this, the area is being downloaded from OpenStreetMap
const SLOW_AFTER_TICKS = 10; // 20s at one message per 2s

interface MapPreviewProps {
  imageUrl: string | null;
  isLoading: boolean;
  aspectRatio: string;
  error?: string | null;
}

export function MapPreview({ imageUrl, isLoading, aspectRatio, error }: MapPreviewProps) {
  const [ticks, setTicks] = useState(0);

  useEffect(() => {
    if (!isLoading) {
      setTicks(0);
      return;
    }

    const interval = setInterval(() => {
      setTicks((prev) => prev + 1);
    }, 2000);

    return () => clearInterval(interval);
  }, [isLoading]);

  return (
    <div className="space-y-3">
      <label className="text-primary-2-80 block text-sm font-medium">Preview</label>
      <div
        className={cn(
          'relative overflow-hidden rounded-lg border-2 border-dashed',
          isLoading ? 'border-secondary-1 animate-pulse' : 'border-neutral-2-20',
          error ? 'border-red-400' : ''
        )}
        style={{ aspectRatio }}
      >
        {isLoading ? (
          <div className="bg-neutral-2-10 absolute inset-0 flex flex-col items-center justify-center">
            <div className="border-secondary-1 h-12 w-12 animate-spin rounded-full border-4 border-t-transparent" />
            <p className="text-neutral-2-60 mt-4 text-sm">{LOADING_MESSAGES[ticks % LOADING_MESSAGES.length]}</p>
            <p className="text-neutral-2-40 mt-1 text-xs">
              {ticks >= SLOW_AFTER_TICKS
                ? 'First time mapping this area. This can take a minute'
                : 'This may take a moment'}
            </p>
          </div>
        ) : imageUrl ? (
          <img
            src={imageUrl}
            alt="Map preview"
            className="h-full w-full object-contain"
          />
        ) : error ? (
          <div className="bg-red-50 absolute inset-0 flex flex-col items-center justify-center p-4">
            <svg
              className="h-12 w-12 text-red-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <p className="mt-2 text-center text-sm text-red-600">{error}</p>
          </div>
        ) : (
          <div className="bg-neutral-2-5 absolute inset-0 flex flex-col items-center justify-center">
            <svg
              className="text-neutral-2-40 h-16 w-16"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
              />
            </svg>
            <p className="text-neutral-2-60 mt-4 text-sm">Search for a city, state or country to see your map</p>
          </div>
        )}
      </div>
    </div>
  );
}
